import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import customerService from '../services/CustomerService';
import appointmentService from '../services/AppointmentService';
import { useAuth } from '../contexts/AuthContext';
import {
  Card,
  Button,
  SearchInput,
  Select,
  CustomDropdown,
  Table,
  Modal,
  Input,
  Alert
} from '../components/ui';

/**
 * CustomersPage component for managing customers
 * @returns {React.ReactElement} CustomersPage component
 */
const CustomersPage = () => {
  // State for customers data
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage] = useState(10);

  // State for creating/editing customers
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    gender: '女',
    age: '',
    address: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const { hasPermission, currentUser } = useAuth();
  const canManageCustomers = hasPermission('customers', 'write');

  // Fetch customers on component mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Function to fetch customers
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const data = customerService.getAllCustomers();
      setCustomers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('获取客户数据失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  // Filter and search customers
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const matchesSearch =
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phoneNumber?.includes(searchTerm) ||
        customer.phone?.includes(searchTerm) ||
        customer.notes?.toLowerCase().includes(searchTerm.toLowerCase());

      if (filter === 'all') return matchesSearch;
      if (filter === 'male') return matchesSearch && customer.gender === '男';
      if (filter === 'female') return matchesSearch && customer.gender === '女';
      if (filter === 'recent') {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return matchesSearch && new Date(customer.createdAt) > oneMonthAgo;
      }
      return matchesSearch;
    });
  }, [customers, searchTerm, filter]);

  // Get current customers for pagination
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle modal open for new customer
  const handleAddNewCustomer = () => {
    setSelectedCustomer(null);
    setFormData({
      name: '',
      phoneNumber: '',
      gender: '女',
      age: '',
      address: '',
      notes: ''
    });
    setFormErrors({});
    setShowCustomerModal(true);
  };

  // Handle modal open for edit customer
  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name || '',
      phoneNumber: customer.phoneNumber || customer.phone || '',
      gender: customer.gender || '女',
      age: customer.age || '',
      address: customer.address || '',
      notes: customer.notes || ''
    });
    setFormErrors({});
    setShowCustomerModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = '请输入客户姓名';
    }

    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = '请输入联系电话';
    } else if (!/^1[3-9]\d{9}$/.test(formData.phoneNumber.trim())) {
      errors.phoneNumber = '请输入正确的手机号码';
    }

    if (formData.age && (formData.age < 0 || formData.age > 120)) {
      errors.age = '请输入有效的年龄';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (selectedCustomer) {
        // Update existing customer
        customerService.updateCustomer(selectedCustomer.id, formData);
      } else {
        // Create new customer
        customerService.createCustomer(formData);
      }

      // Refresh customer list
      fetchCustomers();
      setShowCustomerModal(false);
    } catch (err) {
      console.error('Error saving customer:', err);
      setError('保存客户信息失败');
    }
  };

  // Handle customer delete
  const handleDeleteCustomer = (customerId) => {
    if (window.confirm('确定要删除此客户吗？此操作无法撤销。')) {
      try {
        customerService.deleteCustomer(customerId);
        fetchCustomers();
      } catch (err) {
        console.error('Error deleting customer:', err);
        setError('删除客户失败');
      }
    }
  };

  // Dynamically compute lastAppointment for each customer
  const customersWithLastAppointment = useMemo(() => {
    return customers.map(customer => {
      const appointments = appointmentService.getAppointmentsByCustomer(customer.id);
      const lastAppointment = appointments.length
        ? appointments.sort((a, b) => new Date(b.startTime) - new Date(a.startTime))[0].startTime
        : null;
      return { ...customer, lastAppointment };
    });
  }, [customers]);

  // Table columns configuration
  const columns = [
    {
      key: 'name',
      title: '客户信息',
      render: (_, customer) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg">
              {customer.name ? customer.name.charAt(0) : '?'}
            </div>
          </div>
          <div>
            <Link
              to={`/customers/${customer.id}`}
              className="text-sm font-medium text-gray-900 hover:text-emerald-600 transition-colors duration-200"
            >
              {customer.name || '未命名客户'}
            </Link>
            {customer.ageGroup && (
              <span
                className={
                  "ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium " +
                  (customer.ageGroup === 'youth'
                    ? 'bg-emerald-100 text-emerald-700'
                    : customer.ageGroup === 'adult'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700')
                }
              >
                {customer.ageGroup === 'youth'
                  ? '青少年'
                  : customer.ageGroup === 'adult'
                    ? '成年人'
                    : '老年人'}
              </span>
            )}
            {customer.notes && (
              <p className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                {customer.notes}
              </p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      title: '联系方式',
      render: (_, customer) => (
        <div>
          <div className="text-sm text-gray-900">
            {customer.phoneNumber || customer.phone || '无联系方式'}
          </div>
          {customer.address && (
            <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">{customer.address}</div>
          )}
        </div>
      )
    },
    {
      key: 'info',
      title: '基本信息',
      render: (_, customer) => (
        <div className="text-sm text-gray-900">
          {customer.gender || '未知'} {customer.age ? `/ ${customer.age}岁` : ''}
        </div>
      )
    },
    {
      key: 'createdAt',
      title: '添加时间',
      render: (_, customer) => (
        <div className="text-sm text-gray-500">
          {new Date(customer.createdAt).toLocaleDateString('zh-CN')}
        </div>
      )
    },
    {
      key: 'lastAppointment',
      title: '最近预约',
      render: (_, customer) => (
        customer.lastAppointment ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            {new Date(customer.lastAppointment).toLocaleDateString('zh-CN')}
          </span>
        ) : (
          <span className="text-sm text-gray-500">无预约记录</span>
        )
      )
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, customer) => (
        <div className="flex items-center space-x-2">
          <Link
            to={`/customers/${customer.id}`}
            className="text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors duration-200"
          >
            查看
          </Link>
          {canManageCustomers && (
            <>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => handleEditCustomer(customer)}
                className="text-gray-600 hover:text-gray-700 text-sm font-medium transition-colors duration-200"
              >
                编辑
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => handleDeleteCustomer(customer.id)}
                className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
              >
                删除
              </button>
            </>
          )}
        </div>
      ),
      cellClassName: 'text-right'
    }
  ];

  // Filter options
  const filterOptions = [
    { value: 'all', label: '全部客户' },
    { value: 'male', label: '男性客户' },
    { value: 'female', label: '女性客户' },
    { value: 'recent', label: '最近一个月' }
  ];

  // Gender options
  const genderOptions = [
    { value: '女', label: '女' },
    { value: '男', label: '男' },
    { value: '其他', label: '其他' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              客户管理
            </h1>
            <p className="text-gray-600">
              管理客户信息，查看预约记录和治疗历史
            </p>
          </div>
          {canManageCustomers && (
            <div className="mt-4 sm:mt-0">
              <Button
                onClick={handleAddNewCustomer}
              >
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加客户
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="搜索客户姓名、电话或备注..."
                className="w-full"
              />
            </div>
            <div>
              <CustomDropdown
                options={filterOptions}
                value={filter}
                onChange={handleFilterChange}
                placeholder="选择筛选条件"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>共找到 {filteredCustomers.length} 名客户</span>
            {searchTerm && (
              <span>搜索: "{searchTerm}"</span>
            )}
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Customers Table */}
        <Table
          columns={columns}
          data={currentCustomers.map(c => customersWithLastAppointment.find(c2 => c2.id === c.id) || c)}
          loading={loading}
          variant="simple"
          className="border-emerald-200/50 bg-white/70 backdrop-blur-sm"
          emptyMessage="没有找到客户"
          emptyIcon={
            <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
          emptyAction={
            canManageCustomers ? (
              <Button onClick={handleAddNewCustomer} className="mt-4">
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加客户
              </Button>
            ) : null
          }
        />

        {/* Pagination */}
        {filteredCustomers.length > customersPerPage && (
          <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                显示 <span className="font-medium">{indexOfFirstCustomer + 1}</span> 至{' '}
                <span className="font-medium">
                  {Math.min(indexOfLastCustomer, filteredCustomers.length)}
                </span>{' '}
                条，共 <span className="font-medium">{filteredCustomers.length}</span> 条结果
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>

                <div className="flex items-center space-x-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const shouldDisplay =
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

                    if (!shouldDisplay) {
                      if (pageNumber === 2 || pageNumber === totalPages - 1) {
                        return (
                          <span key={`ellipsis-${pageNumber}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      return null;
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={currentPage === pageNumber ? "primary" : "ghost"}
                        size="small"
                        onClick={() => paginate(pageNumber)}
                        className="min-w-[36px]"
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      <Modal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        title={selectedCustomer ? '编辑客户信息' : '添加新客户'}
        size="large"
        footer={
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={() => setShowCustomerModal(false)}
            >
              取消
            </Button>
            <Button
              type="submit"
              form="customer-form"
            >
              {selectedCustomer ? '保存修改' : '添加客户'}
            </Button>
          </div>
        }
      >
        <form id="customer-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="客户姓名"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={formErrors.name}
              required
              placeholder="请输入客户姓名"
            />

            <Input
              label="联系电话"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              error={formErrors.phoneNumber}
              required
              placeholder="请输入手机号码"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CustomDropdown
              label="性别"
              options={genderOptions}
              value={formData.gender}
              onChange={handleInputChange}
              name="gender"
            />

            <Input
              label="年龄"
              name="age"
              type="number"
              min="0"
              max="120"
              value={formData.age}
              onChange={handleInputChange}
              error={formErrors.age}
              placeholder="请输入年龄"
            />
          </div>

          <Input
            label="地址"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            placeholder="请输入客户地址"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              备注信息
            </label>
            <textarea
              name="notes"
              rows={4}
              value={formData.notes}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
              placeholder="添加额外信息，如健康状况、过敏反应、特殊需求等"
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomersPage;